import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { EventListRow } from '../../data/events';
import { invoiceReviewHoldMessage, updateEventStageStatus } from '../../data/orderStages';
import { fetchLinkedFulfillmentOrderCounts } from '../../data/orderWorkspace';
import { formatOrderStatusLabel } from '../../lib/orderStages';
import {
  canOpenEventFulfillmentScanner,
  defaultEventFulfillmentScanMode,
} from '../../domain/fulfillment/eventFulfillmentScanMode';
import EventConfirmationModal from './EventConfirmationModal';

export type EventStageUpdatePayload = {
  eventId: string;
  status: string;
  reservationJobStatus?: string | null;
};

export type EventActionsDropdownProps = {
  event: EventListRow;
  /** Current order status (from deriveOrderStage) for "Move to next stage". */
  currentOrderStatus: string | null;
  /** Next status in stage order; if null, "Move to next stage" is hidden or disabled. */
  nextOrderStatus: string | null;
  /** Ordered list of statuses (for next-stage label). */
  orderedStatuses: Array<{ status: string; label: string }>;
  onDelete: (eventId: string, name: string | null) => void;
  onStageUpdated?: (payload?: EventStageUpdatePayload) => void;
  onBeginPicking?: (eventId: string) => void;
  /** Stop propagation so card/link click is not triggered. */
  stopPropagation?: boolean;
};

export default function EventActionsDropdown({
  event,
  currentOrderStatus,
  nextOrderStatus,
  orderedStatuses,
  onDelete,
  onStageUpdated,
  onBeginPicking,
  stopPropagation = true,
}: EventActionsDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [stageBusy, setStageBusy] = React.useState(false);
  const [cancelBusy, setCancelBusy] = React.useState(false);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const eventId = event.id;
  const linkedFulfillmentOrderCountsQuery = useQuery({
    queryKey: ['event-linked-fulfillment-orders', eventId],
    enabled: open && Boolean(eventId),
    queryFn: async () => {
      const { data, error } = await fetchLinkedFulfillmentOrderCounts(eventId);
      if (error) throw error;
      return data;
    },
  });
  const linkedTransferOrderCount = linkedFulfillmentOrderCountsQuery.data?.transferOrderCount ?? 0;
  const linkedPurchaseOrderCount = linkedFulfillmentOrderCountsQuery.data?.purchaseOrderCount ?? 0;
  const hasLinkedFulfillmentOrders = linkedTransferOrderCount > 0 || linkedPurchaseOrderCount > 0;
  const nextLabel = nextOrderStatus
    ? orderedStatuses.find(s => s.status === nextOrderStatus)?.label ?? formatOrderStatusLabel(nextOrderStatus)
    : null;

  React.useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 200;
    const menuHeight = 280;
    const margin = 4;
    let top = rect.bottom + margin;
    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    if (top + menuHeight > window.innerHeight - 8) top = rect.top - menuHeight - margin;
    if (top < 8) top = 8;
    setMenuPosition({ top, left });
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const close = () => setOpen(false);

  const handleMoveToNextStage = async () => {
    if (!eventId || !nextOrderStatus) return;
    if (nextOrderStatus === 'pending_acceptance') {
      close();
      navigate(`/events/${eventId}/overview?sendToClient=1`);
      return;
    }
    if (nextOrderStatus === 'confirmed') {
      setConfirmationOpen(true);
      return;
    }
    setStageBusy(true);
    try {
      const { data, error } = await updateEventStageStatus({ eventId, status: nextOrderStatus });
      if (error) throw error;
      if (data?.invoiceReview) {
        window.alert(invoiceReviewHoldMessage(data.invoiceReview));
        close();
        return;
      }
      onStageUpdated?.({ eventId, status: nextOrderStatus });
      close();
    } catch (err) {
      console.error('Failed to update event stage:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to move event to next stage.');
    } finally {
      setStageBusy(false);
    }
  };

  const handleConvertToReservation = () => {
    close();
    navigate(`/events/${event.id}?tab=items`, { state: { convertToReservation: true } });
  };

  const handleCancelLoseOrder = async () => {
    if (!eventId) return;
    if (!window.confirm('Cancel/Lose this event? The event stage will be set to Cancelled.')) return;
    const reason = window.prompt('Please provide a reason for cancelling or losing this event (required):');
    if (reason === null) return;
    if (!(reason != null && String(reason).trim().length > 0)) {
      window.alert('A reason is required.');
      return;
    }
    setCancelBusy(true);
    try {
      const { error } = await updateEventStageStatus({ eventId, status: 'cancelled', reason: String(reason).trim() });
      if (error) throw error;
      close();
      onStageUpdated?.({ eventId, status: 'cancelled' });
    } catch (err) {
      console.error('Failed to cancel event:', err);
      window.alert(err instanceof Error ? err.message : 'Unable to cancel event.');
    } finally {
      setCancelBusy(false);
    }
  };

  const handleDuplicate = () => {
    close();
    navigate(`/events?create=1&duplicateFrom=${event.id}`);
  };

  const handleDelete = () => {
    close();
    onDelete(event.id, event.name);
  };

  const menuContent =
    open && menuPosition ? (
      <div
        ref={menuRef}
        className="dropdown-menu"
        style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          zIndex: 10000,
          minWidth: '200px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dropdown-menu__section">
          {hasLinkedFulfillmentOrders ? (
            <div
              className="dropdown-menu__header"
              role="status"
              aria-live="polite"
              style={{
                color: 'var(--warning-700, var(--warning))',
                borderBottom: '1px solid color-mix(in srgb, var(--warning) 35%, var(--border))',
              }}
            >
              {`${linkedTransferOrderCount} TO${linkedTransferOrderCount === 1 ? '' : 's'} · ${linkedPurchaseOrderCount} PO${linkedPurchaseOrderCount === 1 ? '' : 's'} linked`}
              <br />
              {'Not auto-cancelled on stage change'}
            </div>
          ) : null}
          {eventId && nextOrderStatus && (
            <button
              type="button"
              className="dropdown-menu__item"
              onClick={handleMoveToNextStage}
              disabled={stageBusy}
            >
              {stageBusy ? 'Updating…' : `Move to next stage${nextLabel ? ` (${nextLabel})` : ''}`}
            </button>
          )}
          {(() => {
            const scanMode = defaultEventFulfillmentScanMode(event.status);
            if (canOpenEventFulfillmentScanner(event.status) && onBeginPicking) {
              return (
                <button type="button" className="dropdown-menu__item" onClick={() => { close(); onBeginPicking(event.id); }}>
                  {scanMode === 'returning'
                    ? 'Begin Returning'
                    : 'Begin Picking'}
                </button>
              );
            }
            return (
              <button type="button" className="dropdown-menu__item" onClick={handleConvertToReservation}>
                Convert to reservation
              </button>
            );
          })()}
          {eventId ? (
            <button
              type="button"
              className="dropdown-menu__item"
              onClick={handleCancelLoseOrder}
              disabled={cancelBusy}
            >
              {cancelBusy ? 'Updating…' : 'Cancel/Lose order'}
            </button>
          ) : null}
          <button type="button" className="dropdown-menu__item" onClick={handleDuplicate}>
            Duplicate
          </button>
          <button
            type="button"
            className="dropdown-menu__item"
            onClick={handleDelete}
            style={{ color: 'var(--danger)' }}
          >
            Delete
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        className="btn micro"
        onClick={(e) => {
          if (stopPropagation) {
            e.preventDefault();
            e.stopPropagation();
          }
          setOpen((prev) => !prev);
        }}
        aria-label="Event actions"
        aria-expanded={open}
        style={{ minWidth: '32px', padding: '0.25rem 0.5rem' }}
      >
        ⋯
      </button>
      {typeof document !== 'undefined' && menuContent ? createPortal(menuContent, document.body) : null}
      <EventConfirmationModal
        eventId={eventId}
        eventStatus={currentOrderStatus}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onCompleted={(result) => {
          if (result.confirmation.pendingRequest) {
            setOpen(false);
            return;
          }
          onStageUpdated?.({
            eventId,
            status: result.status || result.confirmation.eventStatus || 'confirmed',
            reservationJobStatus: result.reservationJobStatus,
          });
          setConfirmationOpen(false);
          close();
        }}
        onSendToClient={() => {
          setConfirmationOpen(false);
          close();
          navigate(`/events/${eventId}/overview?sendToClient=1`);
        }}
      />
    </div>
  );
}
